export type RecurrenceFrequency =
  | ''
  | 'DAILY'
  | 'MONTHLY'
  | 'WEEKLY'
  | 'YEARLY';

export interface RecurrenceFormValue {
  count: string;
  extras: string[];
  frequency: RecurrenceFrequency;
  interval: string;
  until: string;
}

export interface ReminderFormValue {
  amount: string;
  unit: 'd' | 'h' | 'm';
}

export function parseRecurrenceForm(value: string): RecurrenceFormValue {
  const parts = new Map(
    value
      .split(';')
      .map(part => part.trim())
      .filter(Boolean)
      .map(part => {
        const [key, ...rest] = part.split('=');
        return [key?.toUpperCase() ?? '', rest.join('=')];
      })
  );
  const frequency = parts.get('FREQ');
  return {
    count: parts.get('COUNT') ?? '',
    extras: value
      .split(';')
      .map(part => part.trim())
      .filter(Boolean)
      .filter(part => {
        const key = part.split('=')[0]?.toUpperCase();
        return !['COUNT', 'FREQ', 'INTERVAL', 'UNTIL'].includes(key ?? '');
      }),
    frequency:
      frequency === 'DAILY' ||
      frequency === 'WEEKLY' ||
      frequency === 'MONTHLY' ||
      frequency === 'YEARLY'
        ? frequency
        : '',
    interval: parts.get('INTERVAL') ?? '1',
    until: icsUntilToDateInput(parts.get('UNTIL') ?? ''),
  };
}

export function formatRecurrenceForm(form: RecurrenceFormValue): string {
  if (!form.frequency) return '';
  const parts = [`FREQ=${form.frequency}`];
  const interval = Number.parseInt(form.interval, 10);
  if (Number.isFinite(interval) && interval > 1) {
    parts.push(`INTERVAL=${interval}`);
  }
  const count = Number.parseInt(form.count, 10);
  if (Number.isFinite(count) && count > 0) {
    parts.push(`COUNT=${count}`);
  } else if (form.until) {
    parts.push(`UNTIL=${dateInputToIcsUntil(form.until)}`);
  }
  parts.push(...form.extras);
  return parts.join(';');
}

export function parseReminderForm(value: string): ReminderFormValue {
  const match = value.trim().match(/^(\d+)\s*([mhd])$/i);
  if (!match) return { amount: '15', unit: 'm' };
  const [, amount = '15', unit = 'm'] = match;
  return {
    amount,
    unit: unit.toLowerCase() as ReminderFormValue['unit'],
  };
}

export function formatReminderForm(form: ReminderFormValue): string {
  const amount = Number.parseInt(form.amount, 10);
  if (!Number.isFinite(amount) || amount <= 0) return '';
  return `${amount}${form.unit}`;
}

export function parseExceptionDates(value: string): string[] {
  return value
    .split(/[,\n;]/)
    .map(item => item.trim())
    .filter(Boolean)
    .flatMap(item => {
      const date = new Date(item);
      if (!Number.isFinite(date.getTime())) return [];
      return [date.toISOString()];
    });
}

export function formatExceptionDates(values: readonly string[]): string {
  return [...new Set(values)].join('\n');
}

function icsUntilToDateInput(value: string): string {
  const match = value.match(/^(\d{4})(\d{2})(\d{2})/);
  if (!match) return '';
  return `${match[1]}-${match[2]}-${match[3]}`;
}

function dateInputToIcsUntil(value: string): string {
  return value.replaceAll('-', '') + 'T235959Z';
}
